import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
  FormLabel,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemSecondaryAction,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Person,
  Email,
  Phone,
  LocationOn,
  Work,
  CheckCircle,
  AccessTime,
  Close,
  Add,
  Map,
  Home,
  Delete,
  StarBorder,
  Settings,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import LocationPicker from '../components/LocationPicker';
import ProfilePaymentMethods from '../components/ProfilePaymentMethods';
import { HANDLER_SITE_URL } from '../config/siteMode';

const Profile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingHandler, setIsEditingHandler] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
  });

  // Address book state
  const [addressBook, setAddressBook] = useState([]);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    label: '',
    formatted_address: '',
    street_number: '',
    street_name: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'South Africa',
    latitude: null,
    longitude: null,
  });
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);

  const [handlerProfile, setHandlerProfile] = useState(null);
  const [handlerFormData, setHandlerFormData] = useState({
    bio: '',
    hourly_rate: 20.00,
    max_pets_per_service: 3,
    preferred_pet_sizes: [],
    service_radius_km: 5,
    service_areas: [],
    is_available: false,
  });
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [applicationData, setApplicationData] = useState({
    motivation: '',
    experience_description: '',
    availability_description: '',
    references: '',
    id_document: null,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || '',
      });

      // Fetch address book
      fetchAddressBook();

      // If user is a handler, fetch handler profile
      if (user.is_handler) {
        fetchHandlerProfile();
      } else if (user.is_owner && !user.is_handler) {
        // If user is only an owner, check application status
        fetchApplicationStatus();
      }
    }
  }, [user]);

  const fetchAddressBook = async () => {
    try {
      const response = await apiService.getAddressBook();
      let addresses = response.results || response;
      
      // Ensure it's an array
      if (!Array.isArray(addresses)) {
        addresses = [];
      }
      
      setAddressBook(addresses);
      
      // If there's only one address, make it default
      if (addresses.length === 1 && !addresses[0].is_default) {
        await apiService.setDefaultAddress(addresses[0].id);
        addresses[0].is_default = true;
        setAddressBook(addresses);
      }
      
      // Find default address
      const defaultAddr = addresses.find(addr => addr.is_default);
      setDefaultAddress(defaultAddr);
    } catch (err) {
      console.error('Error fetching address book:', err);
    }
  };

  const fetchHandlerProfile = async () => {
    try {
      const profile = await apiService.getCurrentUserHandlerProfile();
      setHandlerProfile(profile);
      
      // Populate handler form data
      setHandlerFormData({
        bio: profile.bio || '',
        hourly_rate: profile.hourly_rate || 20.00,
        max_pets_per_service: profile.max_pets_per_walk || 3,
        preferred_pet_sizes: profile.preferred_pet_sizes || [],
        service_radius_km: profile.service_radius_km || 5,
        is_available: profile.is_available || false,
      });
    } catch (err) {
      console.error('Error fetching handler profile:', err);
      setError('Failed to load handler profile.');
    }
  };

  const fetchApplicationStatus = async () => {
    try {
      const status = await apiService.getHandlerApplicationStatus();
      setApplicationStatus(status);
    } catch (err) {
      console.error('Error fetching application status:', err);
    }
  };

  const handleApplicationSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      await apiService.submitHandlerApplication(applicationData);
      
      setSuccess('Handler application submitted successfully! We will review your application and get back to you soon.');
      setApplicationDialogOpen(false);
      
      // Reset form
      setApplicationData({
        motivation: '',
        experience_description: '',
        availability_description: '',
        references: '',
        id_document: null,
      });
      
      // Refresh application status
      await fetchApplicationStatus();
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Failed to submit application. Please try again.');
      console.error('Error submitting application:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationInputChange = (field, value) => {
    setApplicationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setApplicationData(prev => ({
      ...prev,
      id_document: file
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      await apiService.updateProfile(formData);
      
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      console.error('Error updating profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      zip_code: user.zip_code || '',
    });
    setIsEditing(false);
  };

  const handleHandlerInputChange = (field, value) => {
    setHandlerFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleHandlerSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      await apiService.updateCurrentUserHandlerProfile(handlerFormData);
      
      setSuccess('Handler profile updated successfully!');
      setIsEditingHandler(false);
      
      // Refresh handler profile data
      await fetchHandlerProfile();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update handler profile. Please try again.');
      console.error('Error updating handler profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHandlerCancel = () => {
    if (handlerProfile) {
      setHandlerFormData({
        bio: handlerProfile.bio || '',
        hourly_rate: handlerProfile.hourly_rate || 20.00,
        max_pets_per_service: handlerProfile.max_pets_per_walk || 3,
        preferred_pet_sizes: handlerProfile.preferred_pet_sizes || [],
        service_radius_km: handlerProfile.service_radius_km || 5,
        is_available: handlerProfile.is_available || false,
      });
    }
    setIsEditingHandler(false);
  };

  const handlePetSizeToggle = (size) => {
    const current = isEditingHandler ? handlerFormData.preferred_pet_sizes : handlerProfile.preferred_pet_sizes;
    const updated = current.includes(size)
      ? current.filter(s => s !== size)
      : [...current, size];
    
    if (isEditingHandler) {
      handleHandlerInputChange('preferred_pet_sizes', updated);
    }
  };

  // Address book functions
  const handleAddressFormChange = (field, value) => {
    setAddressFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAddress = async () => {
    try {
      setAddressLoading(true);
      setError('');
      
      // Validation
      if (!addressFormData.label.trim()) {
        setError('Please provide an address label.');
        return;
      }
      
      if (!addressFormData.formatted_address) {
        setError('Please select a valid location using the search or GPS.');
        return;
      }
      
      if (!addressFormData.latitude || !addressFormData.longitude) {
        setError('Please ensure latitude and longitude are set.');
        return;
      }
      
      console.log('Saving address data:', addressFormData);
      
      if (editingAddressId) {
        // Update existing address
        await apiService.updateAddress(editingAddressId, addressFormData);
      } else {
        // Create new address
        await apiService.createAddress(addressFormData);
      }
      
      setSuccess(editingAddressId ? 'Address updated successfully!' : 'Address saved successfully!');
      await fetchAddressBook();
      setAddEditDialogOpen(false);
      setEditingAddressId(null);
      resetAddressForm();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.message || 'Failed to save address. Please try again.';
      setError(errorMessage);
      console.error('Error saving address:', err);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      setAddressLoading(true);
      await apiService.deleteAddress(addressId);
      setSuccess('Address deleted successfully!');
      await fetchAddressBook();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete address. Please try again.');
      console.error('Error deleting address:', err);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      setAddressLoading(true);
      await apiService.setDefaultAddress(addressId);
      setSuccess('Default address updated!');
      await fetchAddressBook();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to set default address. Please try again.');
      console.error('Error setting default address:', err);
    } finally {
      setAddressLoading(false);
    }
  };

  const openAddressDialog = (address = null) => {
    if (address) {
      setEditingAddressId(address.id);
      setAddressFormData({
        label: address.label || '',
        formatted_address: address.formatted_address || '',
        street_number: address.street_number || '',
        street_name: address.street_name || '',
        city: address.city || '',
        state: address.state || '',
        postal_code: address.postal_code || '',
        country: address.country || 'South Africa',
        latitude: address.latitude,
        longitude: address.longitude,
      });
    } else {
      setEditingAddressId(null);
      resetAddressForm();
    }
    setAddEditDialogOpen(true);
  };

  const resetAddressForm = () => {
    setAddressFormData({
      label: '',
      formatted_address: '',
      street_number: '',
      street_name: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'South Africa',
      latitude: null,
      longitude: null,
    });
  };

  const handleLocationPickerChange = (locationData) => {
    console.log('LocationPicker data received:', locationData);
    
    if (locationData.latitude && locationData.longitude) {
      // Extract address components if available
      const addressComponents = locationData.addressComponents || {};
      
      const updatedData = {
        ...addressFormData,
        formatted_address: locationData.formattedAddress || locationData.address || '',
        street_number: addressComponents.streetNumber || '',
        street_name: addressComponents.streetName || '',
        city: addressComponents.city || '',
        state: addressComponents.state || '',
        postal_code: addressComponents.postalCode || '',
        country: addressComponents.country || 'South Africa',
        latitude: parseFloat(locationData.latitude),
        longitude: parseFloat(locationData.longitude),
      };
      
      console.log('Address form data updated:', updatedData);
      setAddressFormData(updatedData);
    }
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserTypes = () => {
    const types = [];
    if (user?.is_owner) types.push('Owner');
    if (user?.is_handler) types.push('Handler');
    return types;
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Profile
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your account information
        </Typography>
      </Box>

      {/* Success/Error Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Overview Card */}
        <Grid size={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative', mr: 3 }}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    fontSize: '2rem',
                    bgcolor: 'primary.main'
                  }}
                >
                  {getUserInitials()}
                </Avatar>
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    border: '2px solid',
                    borderColor: 'background.paper',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                  size="small"
                >
                  <PhotoCamera fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {user.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {user.email}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {getUserTypes().map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      color={type === 'Handler' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Member since {new Date(user.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              <Box>
                {!isEditing ? (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setIsEditing(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit Profile
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSave}
                      disabled={loading}
                      sx={{ textTransform: 'none' }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Personal Information */}
        <Grid size={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Personal Information
                </Typography>
              </Box>
              
              <Box>
                {!isEditing ? (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setIsEditing(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Update Profile
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSave}
                      disabled={loading}
                      sx={{ textTransform: 'none' }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone_number}
                  onChange={(e) => handleInputChange('phone_number', e.target.value)}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Address Information */}
        <Grid size={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Address Information
                </Typography>
              </Box>
              
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => setAddressDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Manage Addresses
              </Button>
            </Box>
            
            {defaultAddress ? (
              <Paper sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Home sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {defaultAddress.label}
                  </Typography>
                  <Chip
                    label="Default"
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {defaultAddress.formatted_address}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {defaultAddress.city && defaultAddress.state && 
                    `${defaultAddress.city}, ${defaultAddress.state}`
                  }
                  {defaultAddress.postal_code && ` ${defaultAddress.postal_code}`}
                </Typography>
              </Paper>
            ) : (
              <Paper sx={{ p: 3, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300', textAlign: 'center' }}>
                <LocationOn sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Default Address Set
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Add an address to your address book to set as your default location.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => openAddressDialog()}
                  sx={{ textTransform: 'none' }}
                >
                  Add Your First Address
                </Button>
              </Paper>
            )}
          </Card>
        </Grid>

        {/* Payment Methods */}
        <Grid size={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Payment Methods
              </Typography>
            </Box>
            <ProfilePaymentMethods />
          </Card>
        </Grid>

        {/* Handler Profile Section */}
        {user.is_handler && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Handler Profile
                </Typography>
                
                {handlerProfile && (
                  <Box>
                    {!isEditingHandler ? (
                      <Button
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => setIsEditingHandler(true)}
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                      >
                        Update Handler Profile
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={<Save />}
                          onClick={handleHandlerSave}
                          disabled={loading}
                          sx={{ textTransform: 'none' }}
                        >
                          Save Changes
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Cancel />}
                          onClick={handleHandlerCancel}
                          disabled={loading}
                          sx={{ textTransform: 'none' }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              
              {handlerProfile ? (
                <Grid container spacing={3}>
                  {/* Bio */}
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Bio"
                      value={isEditingHandler ? handlerFormData.bio : handlerProfile.bio}
                      onChange={(e) => handleHandlerInputChange('bio', e.target.value)}
                      disabled={!isEditingHandler}
                      variant={isEditingHandler ? 'outlined' : 'filled'}
                      placeholder="Tell pet owners about your experience and passion for pets..."
                      helperText={isEditingHandler ? `${handlerFormData.bio.length}/500 characters` : ''}
                      inputProps={{ maxLength: 500 }}
                    />
                  </Grid>
                  
                  {/* Experience and Rate */}

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Hourly Rate (Rand)"
                      value={isEditingHandler ? handlerFormData.hourly_rate : handlerProfile.hourly_rate}
                      onChange={(e) => handleHandlerInputChange('hourly_rate', parseFloat(e.target.value) || 0)}
                      disabled={!isEditingHandler}
                      variant={isEditingHandler ? 'outlined' : 'filled'}
                      inputProps={{ min: 10, max: 200, step: 5 }}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>R</Typography>,
                      }}
                    />
                  </Grid>
                  
                  {/* Service Settings */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Pets Per Service"
                      value={isEditingHandler ? handlerFormData.max_pets_per_service : handlerProfile.max_pets_per_walk}
                      onChange={(e) => handleHandlerInputChange('max_pets_per_service', parseInt(e.target.value) || 1)}
                      disabled={!isEditingHandler}
                      variant={isEditingHandler ? 'outlined' : 'filled'}
                      inputProps={{ min: 1, max: 10 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Service Radius (Kilometers)"
                      value={isEditingHandler ? handlerFormData.service_radius_km : handlerProfile.service_radius_km}
                      onChange={(e) => handleHandlerInputChange('service_radius_km', parseInt(e.target.value) || 1)}
                      disabled={!isEditingHandler}
                      variant={isEditingHandler ? 'outlined' : 'filled'}
                      inputProps={{ min: 1, max: 80 }}
                    />
                  </Grid>
                  

                  
                  {/* Service Radius */}
                  <Grid size={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                      <Map sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                      Service Radius
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Set the maximum distance you're willing to travel for pet services
                    </Typography>
                    
                    {isEditingHandler ? (
                      <Box>
                        <TextField
                          type="number"
                          value={handlerFormData.service_radius_km}
                          onChange={(e) => handleHandlerInputChange('service_radius_km', parseInt(e.target.value) || 5)}
                          inputProps={{ min: 1, max: 50 }}
                          size="small"
                          sx={{ width: 200 }}
                          helperText="Maximum distance in kilometers (1-50 km)"
                        />
                      </Box>
                    ) : (
                      <Typography variant="body1">
                        {handlerProfile.service_radius_km || 5} km
                      </Typography>
                    )}
                  </Grid>
                  
                  {/* Preferred Pet Sizes */}
                  <Grid size={12}>
                    <FormLabel component="legend" sx={{ mb: 1 }}>
                      Preferred Pet Sizes
                    </FormLabel>
                    <FormGroup row>
                      {['small', 'medium', 'large'].map((size) => (
                        <FormControlLabel
                          key={size}
                          control={
                            <Checkbox
                              checked={
                                isEditingHandler 
                                  ? handlerFormData.preferred_pet_sizes.includes(size)
                                  : handlerProfile.preferred_pet_sizes?.includes(size) || false
                              }
                              onChange={() => handlePetSizeToggle(size)}
                              disabled={!isEditingHandler}
                            />
                          }
                          label={size.charAt(0).toUpperCase() + size.slice(1)}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                  
                  {/* Availability */}
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            isEditingHandler 
                              ? handlerFormData.is_available 
                              : handlerProfile.is_available || false
                          }
                          onChange={(e) => handleHandlerInputChange('is_available', e.target.checked)}
                          disabled={!isEditingHandler}
                        />
                      }
                      label="Available for services"
                    />
                  </Grid>
                  
                  {/* Read-only Stats */}
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Handler Statistics
                    </Typography>
                    <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Rating
                        </Typography>
                        <Typography variant="body1">
                          {handlerProfile.average_rating > 0 ? `${handlerProfile.average_rating}/5` : 'No ratings yet'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Services
                        </Typography>
                        <Typography variant="body1">
                          {handlerProfile.total_services_completed}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Reviews
                        </Typography>
                        <Typography variant="body1">
                          {handlerProfile.total_reviews}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Verification Status
                        </Typography>
                        <Typography variant="body1">
                          {handlerProfile.background_check_verified && handlerProfile.insurance_verified 
                            ? 'Verified' 
                            : 'Pending Verification'
                          }
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Handler profile not set up yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Handler Application Section - Show only for owners who are not yet handlers */}
        {user.is_owner && !user.is_handler && (
          <Grid size={12}>
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Work sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Become a Handler
                </Typography>
              </Box>
              
              {applicationStatus?.has_application ? (
                // Show application status
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {applicationStatus.status === 'pending' && <AccessTime sx={{ mr: 1, color: 'warning.main' }} />}
                    {applicationStatus.status === 'approved' && <CheckCircle sx={{ mr: 1, color: 'success.main' }} />}
                    {applicationStatus.status === 'rejected' && <Close sx={{ mr: 1, color: 'error.main' }} />}
                    <Typography variant="h6">
                      Application Status: {applicationStatus.status_display || applicationStatus.status}
                    </Typography>
                  </Box>
                  
                  {applicationStatus.status === 'pending' && (
                    <Typography variant="body1" color="text.secondary">
                      Your handler application is being reviewed. We'll notify you once a decision has been made.
                    </Typography>
                  )}
                  
                  {applicationStatus.status === 'approved' && (
                    <Typography variant="body1" color="success.main">
                      Congratulations! Your handler application has been approved. You can now access the handler dashboard.
                    </Typography>
                  )}
                  
                  {applicationStatus.status === 'rejected' && (
                    <Typography variant="body1" color="error.main">
                      Your handler application was not approved at this time. You may submit a new application in the future.
                    </Typography>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Submitted: {new Date(applicationStatus.submitted_at).toLocaleDateString()}
                  </Typography>
                </Box>
              ) : (
                // Show apply button and description
                <Box>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Join our community of trusted pet handlers! Earn money while spending time with amazing pets in your neighborhood.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Benefits of becoming a handler:</Typography>
                    <Typography variant="body2" color="text.secondary" component="div">
                      • Flexible scheduling - work when you want
                      <br />
                      • Competitive rates starting at R15/hour
                      <br />
                      • Meet wonderful pets and their families
                      <br />
                      • Get exercise while you earn
                      <br />
                      • Insurance coverage provided
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="contained"
                    startIcon={<Work />}
                    href={HANDLER_SITE_URL}
                    sx={{ textTransform: 'none' }}
                  >
                    Apply to Become a Handler
                  </Button>
                </Box>
              )}
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Handler Application Dialog */}
      <Dialog
        open={applicationDialogOpen}
        onClose={() => setApplicationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Apply to Become a Handler</Typography>
            <IconButton onClick={() => setApplicationDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Why do you want to become a handler?"
                  value={applicationData.motivation}
                  onChange={(e) => handleApplicationInputChange('motivation', e.target.value)}
                  placeholder="Tell us about your passion for pets and why you'd like to join our handler community..."
                  required
                />
              </Grid>
              
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Describe your experience with pets"
                  value={applicationData.experience_description}
                  onChange={(e) => handleApplicationInputChange('experience_description', e.target.value)}
                  placeholder="Share your experience caring for, training, or spending time with pets..."
                  required
                />
              </Grid>
              
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="When are you typically available?"
                  value={applicationData.availability_description}
                  onChange={(e) => handleApplicationInputChange('availability_description', e.target.value)}
                  placeholder="Let us know your general availability (days, times, etc.)..."
                  required
                />
              </Grid>
              
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="References (optional)"
                  value={applicationData.references}
                  onChange={(e) => handleApplicationInputChange('references', e.target.value)}
                  placeholder="Provide contact information for references who can speak to your character and experience with pets..."
                />
              </Grid>
              
              <Grid size={12}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload ID Document (optional but recommended)
                  </Typography>
                  <input
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    id="id-document-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="id-document-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCamera />}
                      sx={{ textTransform: 'none' }}
                    >
                      Upload ID Document
                    </Button>
                  </label>
                  {applicationData.id_document && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Selected: {applicationData.id_document.name}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplicationSubmit}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={20} /> : 'Submit Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Management Dialog */}
      <Dialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Manage Address Book</Typography>
            <IconButton onClick={() => setAddressDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Add New Address Button */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openAddressDialog()}
                sx={{ textTransform: 'none' }}
              >
                Add New Address
              </Button>
            </Box>

            {/* Address List */}
            {addressBook.length > 0 ? (
              <List>
                {addressBook.map((address) => (
                  <ListItem
                    key={address.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                      mb: 2,
                      bgcolor: address.is_default ? 'primary.50' : 'background.paper'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Home sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {address.label}
                        </Typography>
                        {address.is_default && (
                          <Chip
                            label="Default"
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {address.formatted_address}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {address.city && address.state && 
                          `${address.city}, ${address.state}`
                        }
                        {address.postal_code && ` ${address.postal_code}`}
                      </Typography>
                    </Box>
                    
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {!address.is_default && (
                          <Tooltip title="Set as default">
                            <IconButton
                              size="small"
                              onClick={() => handleSetDefaultAddress(address.id)}
                              disabled={addressLoading}
                            >
                              <StarBorder />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit address">
                          <IconButton
                            size="small"
                            onClick={() => openAddressDialog(address)}
                            disabled={addressLoading}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete address">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAddress(address.id)}
                            disabled={addressLoading}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                <LocationOn sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Addresses Saved
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add your first address to get started.
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Address Dialog */}
      <Dialog
        open={addEditDialogOpen}
        onClose={() => {
          setAddEditDialogOpen(false);
          setEditingAddressId(null);
          resetAddressForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editingAddressId ? 'Edit Address' : 'Add New Address'}
            </Typography>
            <IconButton onClick={() => {
              setAddEditDialogOpen(false);
              setEditingAddressId(null);
              resetAddressForm();
            }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Address Label"
                  value={addressFormData.label}
                  onChange={(e) => handleAddressFormChange('label', e.target.value)}
                  placeholder="e.g., Home, Work, Gym..."
                  required
                  helperText="Give this address a memorable name"
                />
              </Grid>
              
              <Grid size={12}>
                <LocationPicker
                  initialLatitude={addressFormData.latitude}
                  initialLongitude={addressFormData.longitude}
                  initialAddress={addressFormData.formatted_address}
                  onLocationChange={handleLocationPickerChange}
                  showAddressBook={false}
                  allowSaveToAddressBook={false}
                  placeholder="Search for the address location..."
                  label="Address Location"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setAddEditDialogOpen(false);
              setEditingAddressId(null);
              resetAddressForm();
            }} 
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAddress}
            disabled={addressLoading || !addressFormData.label || !addressFormData.latitude}
            sx={{ textTransform: 'none' }}
          >
            {addressLoading ? <CircularProgress size={20} /> : (editingAddressId ? 'Update Address' : 'Save Address')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile; 